import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaUserShield, 
  FaSave, 
  FaTimes,
  FaKey,
  FaCheck
} from 'react-icons/fa';
import { roleService, CreateRoleData, Permission } from '../services/roleService';
import { usePageTitle } from '../hooks/usePageTitle';
import { useHiddenFields } from '../hooks/useHiddenFields';
import '../css/pages/AddRole.css';

const AddRole: React.FC = () => {
  usePageTitle();
  const navigate = useNavigate();
  const { isHiddenModeActive } = useHiddenFields();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [groupedPermissions, setGroupedPermissions] = useState<Record<string, Permission[]>>({});
  
  const [formData, setFormData] = useState<CreateRoleData>({
    name: '',
    description: '',
    permissionIds: []
  });
  
  const [errors, setErrors] = useState<string[]>([]);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadPermissions();
  }, []); // Remove dependency on isHiddenModeActive

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const response = await roleService.getPermissions();
      
      // Show ALL permissions when creating/editing roles, regardless of hidden mode
      setPermissions(response.permissions);
      setGroupedPermissions(response.groupedPermissions);
      
      // Expand all modules by default
      setExpandedModules(new Set(Object.keys(response.groupedPermissions)));
    } catch (error) {
      console.error('Error loading permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CreateRoleData, value: any) => {
    setFormData({ ...formData, [field]: value });
    setErrors([]);
  };

  const handlePermissionToggle = (permissionId: number) => {
    const { permissionIds } = formData;
    const updatedIds = permissionIds.includes(permissionId)
      ? permissionIds.filter(id => id !== permissionId)
      : [...permissionIds, permissionId];
    
    handleInputChange('permissionIds', updatedIds);
  };

  const handleModuleToggle = (module: string) => {
    const modulePermissions = groupedPermissions[module] || [];
    const modulePermissionIds = modulePermissions.map(p => p.id);
    const { permissionIds } = formData;
    
    const allSelected = modulePermissionIds.every(id => permissionIds.includes(id));
    
    if (allSelected) {
      // Remove all module permissions
      const updatedIds = permissionIds.filter(id => !modulePermissionIds.includes(id));
      handleInputChange('permissionIds', updatedIds);
    } else {
      // Add all module permissions
      const combinedSet = new Set([...permissionIds, ...modulePermissionIds]);
      const updatedIds = Array.from(combinedSet);
      handleInputChange('permissionIds', updatedIds);
    }
  };

  const toggleModuleExpansion = (module: string) => {
    const updated = new Set(expandedModules);
    if (updated.has(module)) {
      updated.delete(module);
    } else {
      updated.add(module);
    }
    setExpandedModules(updated);
  };

  const validateForm = (): boolean => {
    const validationErrors = roleService.validateRoleData(formData);
    setErrors(validationErrors);
    return validationErrors.length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      await roleService.createRole(formData);
      navigate('/auth/roles');
    } catch (error) {
      console.error('Error creating role:', error);
      setErrors([error instanceof Error ? error.message : 'Failed to create role']);
    } finally {
      setSubmitting(false);
    }
  };

  const getModuleSelectionStatus = (module: string) => {
    const modulePermissions = groupedPermissions[module] || [];
    const modulePermissionIds = modulePermissions.map(p => p.id);
    const selectedCount = modulePermissionIds.filter(id => formData.permissionIds.includes(id)).length;
    
    if (selectedCount === 0) return 'none';
    if (selectedCount === modulePermissionIds.length) return 'all';
    return 'partial';
  };

  if (loading) {
    return <div className="loading">Loading permissions...</div>;
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div style={{ color: '#1a1a1a' }}>
          <h1 style={{ color: '#1a1a1a', margin: '0 0 4px 0' }}><FaUserShield className="page-icon" style={{ marginRight: '8px' }} /> Add New Role</h1>
        </div>
        <button 
          className="back-btn"
          onClick={() => navigate('/auth/roles')}
        >
          <FaArrowLeft />
        </button>
      </div>

      {/* Form */}
      <div className="form-container">
        <div className="form-section">
          <h2>Role Information</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Role Name *</label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter role name"
                className={errors.some(e => e.includes('name')) ? 'error' : ''}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <input
                id="description"
                type="text"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter role description (optional)"
              />
            </div>
          </div>
        </div>

        {/* Permissions Section */}
        <div className="form-section">
          <div className="permissions-header">
            <h2>Permissions</h2>
            <div className="permissions-summary">
              <FaKey className="summary-icon" />
              <span>{formData.permissionIds.length} of {permissions.length} permissions selected</span>
            </div>
          </div>

          <div className="permissions-container">
            {Object.entries(groupedPermissions).map(([module, modulePermissions]) => {
              const selectionStatus = getModuleSelectionStatus(module);
              const isExpanded = expandedModules.has(module);
              
              return (
                <div key={module} className="permission-module">
                  <div className="module-header">
                    <button
                      className="module-toggle"
                      onClick={() => toggleModuleExpansion(module)}
                    >
                      <span className={`toggle-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
                    </button>
                    
                    <div className="module-info">
                      <h3>{module}</h3>
                      <span className="module-count">
                        {modulePermissions.filter(p => formData.permissionIds.includes(p.id)).length} / {modulePermissions.length} selected
                      </span>
                    </div>
                    
                    <button
                      className={`module-select-btn ${selectionStatus}`}
                      onClick={() => handleModuleToggle(module)}
                      title={selectionStatus === 'all' ? 'Deselect all' : 'Select all'}
                    >
                      {selectionStatus === 'all' ? <FaTimes /> : <FaCheck />}
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="permissions-grid">
                      {modulePermissions.map(permission => (
                        <div key={permission.id} className="permission-item">
                          <label className="permission-checkbox">
                            <input
                              type="checkbox"
                              checked={formData.permissionIds.includes(permission.id)}
                              onChange={() => handlePermissionToggle(permission.id)}
                            />
                            <span className="checkmark"></span>
                            <div className="permission-info">
                              <span className="permission-name">{permission.name}</span>
                              <span className="permission-description">{permission.description}</span>
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Errors */}
        {errors.length > 0 && (
          <div className="form-errors">
            <h4>Please fix the following errors:</h4>
            <ul>
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Actions */}
        <div className="form-actions">
          <button 
            className="btn-secondary"
            onClick={() => navigate('/auth/roles')}
          >
            Cancel
          </button>
          <button 
            className="btn-primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            <FaSave />
            {submitting ? 'Creating...' : 'Create Role'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddRole;
